import type { ReactNode } from 'react';
import { Box, Group, Progress, Stack, Text } from '@mantine/core';

import { PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';

interface BoardKpiCardProps {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  /** Default: var(--color-accent-primary) (Figma: svi KPI brojevi su primary-obojani, ne crni). */
  valueColor?: string;
  caption?: string;
  /** Default: var(--color-text-secondary) (neutralna napomena). Crvena za financijske alarme (Nenaplaćeno/Dospjelo). */
  captionColor?: string;
  /** 0-100 — kad je zadano, prikazuje traku ispod vrijednosti (npr. "Realizacija"). */
  progressPercent?: number;
  /** Kad je zadano, prikazuje info ikonu s tooltipom u gornjem desnom kutu kartice. ReactNode (ne
   * samo string) da tooltip može biti bold uvod + popis uvjeta (vidi board.alerts.title obrazac),
   * ne samo jedna rečenica. */
  info?: ReactNode;
  /** Širina tooltipa (px) — veća za bulleted sadržaj s više uvjeta. Default prati PanelInfoIcon (240). */
  infoWidth?: number;
  onClick?: () => void;
}

const cardStyle = {
  borderRadius: 12,
  backgroundColor: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border-soft)'
};

/** Zajednička KPI kartica za oba Board pogleda (BD-01/BD-02) — ikona + label + vrijednost + opcionalna napomena/traka. */
export const BoardKpiCard = ({
  icon,
  label,
  value,
  valueColor = 'var(--color-accent-primary)',
  caption,
  captionColor = 'var(--color-text-secondary)',
  progressPercent,
  info,
  infoWidth,
  onClick
}: BoardKpiCardProps) => {
  const infoIcon = info && (
    <Box style={{ display: 'flex', flexShrink: 0 }} onClick={(event) => event.stopPropagation()}>
      <PanelInfoIcon label={info} width={infoWidth} title={label} />
    </Box>
  );

  const content = (
    <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap={8} wrap="nowrap" align="center">
          {icon && <div style={{ color: 'var(--color-text-muted)', display: 'flex' }}>{icon}</div>}
          <Text
            fz={{ base: 11, sm: 12 }}
            fw={600}
            lh={1}
            tt="uppercase"
            style={{
              color: 'var(--color-text-muted)',
              letterSpacing: 0.4,
              whiteSpace: onClick ? 'nowrap' : undefined
            }}
          >
            {label}
          </Text>
          {onClick && infoIcon}
        </Group>
        {!onClick && infoIcon}
      </Group>
      <Text fz={{ base: 22, sm: 28 }} fw={700} lh={1.3} style={{ color: valueColor }}>
        {value}
      </Text>
      {progressPercent != null && (
        <Progress value={Math.max(0, Math.min(100, progressPercent))} size={6} radius="xl" />
      )}
      {caption && (
        <Text fz={{ base: 11.5, sm: 12 }} style={{ color: captionColor }}>
          {caption}
        </Text>
      )}
    </Stack>
  );

  if (!onClick) {
    return (
      <Stack p={{ base: 14, sm: 18 }} style={{ ...cardStyle, height: '100%' }}>
        {content}
      </Stack>
    );
  }

  return (
    <Group
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      gap={8}
      p={{ base: 14, sm: 18 }}
      wrap="nowrap"
      className="kpi-card-clickable"
      style={{ ...cardStyle, height: '100%', cursor: 'pointer' }}
    >
      {content}
      <Text
        fz={16}
        fw={500}
        style={{ color: 'var(--color-text-muted)', opacity: 0.4, flexShrink: 0 }}
      >
        →
      </Text>
    </Group>
  );
};
